import ErrorBoundary from "components/ErrorBoundary";
import ScrollToTop from "components/ScrollToTop";
import DevDebug from "pages/dev-debug";
import NotFound from "pages/NotFound";
import { HashRouter, Route, Routes as RouterRoutes, Navigate } from "react-router-dom";
import { AuthProvider } from "./contexts/AuthContext";
import { useGoogleAnalytics } from './hooks/useGoogleAnalytics';
import InteractiveMap from './pages/interactive-map';
import Login from './pages/login';
import MainStorefronts from './pages/main-storefronts';
import Register from './pages/register';
import StoreDetails from './pages/store-details';
import UserDashboard from './pages/user-dashboard';
import AdminVetrine from './pages/admin-vetrine';
import AdminAnalytics from './pages/admin-analytics';
import AdminPromotions from './pages/admin-promotions';
import AdminPromotionsCalendar from './pages/admin-promotions-calendar';
import AdminReports from './pages/admin-reports';
import AdminStorefrontForm from './pages/admin-storefront-form';
import AdminDashboard from './pages/admin-dashboard';
import TestInsertForm from './pages/test-insert-form';
import AdminInsertVetrina from './pages/admin-insert-vetrina';
import PublicVetrine from './pages/public-vetrine';
import ProtectedRoute from './components/ProtectedRoute';
import AdminLogin from './pages/admin-login';
import AdminContentManagement from './pages/admin-content-management';
import HomePage from './pages/home';
import HomeServizi from './pages/home-servizi';
import VetrineCivitanova from './pages/vetrine-civitanova';
import VetrineTolentino from './pages/vetrine-tolentino';
import VetrineCamerino from './pages/vetrine-camerino';
import VetrineMacerata from './pages/vetrine-macerata';
import Food from './pages/food';
import FoodVendorsList from './pages/food/city-vendors';
import FoodVendorProducts from './pages/food/vendor-products';
import FoodCart from './pages/food/cart';
import FoodCheckout from './pages/food/checkout';
import OrderConfirmation from './pages/food/order-confirmation';
import AdminFoodOrders from './pages/admin-food-orders';
import Servizi from './pages/servizi';
import RassegnaStampa from './pages/rassegna-stampa';
import RicaricheTelefoniche from './pages/ricariche-telefoniche';
import FarmaCityService from './pages/farma-service-city';
import InvioRicetta from './pages/farma-service-city/invio-ricetta';
import OrariTurni from './pages/farma-service-city/orari-turni';
import CentriMedici from './pages/farma-service-city/centri-medici';
import UniDayMc from './pages/unidaymc/index';
import AdesioneCommercianti from './pages/adesione-commercianti';

// Test route component
function RouteTest() {
  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-100">
      <div className="bg-white p-8 rounded-lg shadow-lg max-w-2xl">
        <h1 className="text-4xl font-bold text-green-600 mb-6">ROUTE TEST OK</h1>
        <div className="space-y-3 text-left text-sm">
          <div className="border-b pb-2">
            <span className="font-semibold">window.location.href:</span>
            <div className="mt-1 text-gray-700 break-all">{window.location?.href}</div>
          </div>
        </div>
      </div>
    </div>
  );
}
// Simple admin route test component (no auth, no redirect, no query)
function AdminRouteTest() {
  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-100">
      <div className="bg-white p-8 rounded-lg shadow-lg max-w-2xl">
        <h1 className="text-4xl font-bold text-blue-600 mb-6">ADMIN ROUTE HIT</h1>
        <div className="space-y-3 text-left text-sm">
          <div className="border-b pb-2">
            <span className="font-semibold">window.location.href:</span>
            <div className="mt-1 text-gray-700 break-all">{window.location?.href}</div>
          </div>
        </div>
      </div>
    </div>
  );
}
// Component to track analytics inside router context
function GoogleAnalyticsTracker() {
  useGoogleAnalytics();
  return null;
}
const Routes = () => {
  return (
    <HashRouter>
      <ErrorBoundary>
        <AuthProvider>
          <GoogleAnalyticsTracker />
          <ScrollToTop />
          <RouterRoutes>
            {/* Test routes - ALWAYS accessible */}
            <Route path="/__route-test" element={<RouteTest />} />
            <Route path="/admin-insert-vetrina" element={<AdminInsertVetrina />} />
            {/* Root route redirects to homepage */}
            <Route path="/" element={<Navigate to="/homepage" replace />} />
            {/* Define your route here */}
            <Route path="/homepage" element={<HomePage />} />
            <Route path="/home" element={<HomeServizi />} />
            <Route path="/home-servizi" element={<HomeServizi />} />
            <Route path="/unidaymc" element={<UniDayMc />} />
            <Route path="/vetrine/macerata" element={<VetrineMacerata />} />
            <Route path="/vetrine/civitanova-marche" element={<VetrineCivitanova />} />
            <Route path="/vetrine/tolentino" element={<VetrineTolentino />} />
            <Route path="/vetrine/camerino" element={<VetrineCamerino />} />
            <Route path="/food" element={<Food />} />
            {/* Specific food routes MUST come before dynamic routes */}
            <Route path="/food/cart" element={<FoodCart />} />
            <Route path="/food/checkout" element={<FoodCheckout />} />
            <Route path="/food/order-confirmation" element={<OrderConfirmation />} />
            {/* Dynamic food routes come after specific routes */}
            <Route path="/food/:city" element={<FoodVendorsList />} />
            <Route path="/food/:city/:vendor" element={<FoodVendorProducts />} />
            <Route path="/servizi" element={<Servizi />} />
            <Route path="/rassegna-stampa" element={<RassegnaStampa />} />
            <Route path="/ricariche-telefoniche" element={<RicaricheTelefoniche />} />
            <Route path="/farma-service-city" element={<FarmaCityService />} />
            <Route path="/farma-service-city/invio-ricetta" element={<InvioRicetta />} />
            <Route path="/farma-service-city/orari-turni" element={<OrariTurni />} />
            <Route path="/farma-service-city/centri-medici" element={<CentriMedici />} />
            <Route path="/adesione-commercianti" element={<AdesioneCommercianti />} />
            <Route path="/main-storefronts" element={<MainStorefronts />} />
            <Route path="/login" element={<Login />} />
            <Route path="/interactive-map" element={<InteractiveMap />} />
            <Route path="/store-details" element={<StoreDetails />} />
            <Route path="/user-dashboard" element={<UserDashboard />} />
            <Route path="/register" element={<Register />} />
            <Route path="/admin-dashboard" element={<AdminDashboard />} />
            <Route path="/admin-vetrine" element={<AdminVetrine />} />
            <Route path="/admin-storefront-form" element={<AdminStorefrontForm />} />
            <Route path="/admin-promotions" element={<AdminPromotions />} />
            <Route path="/admin-promotions-calendar" element={<AdminPromotionsCalendar />} />
            <Route path="/admin-analytics" element={<AdminAnalytics />} />
            <Route path="/admin-reports" element={<AdminReports />} />
            <Route path="/admin-login" element={<AdminLogin />} />
            <Route path="/admin-content-management" element={<AdminContentManagement />} />
            <Route path="/admin-food-orders" element={<AdminFoodOrders />} />
            <Route path="/test-insert-form" element={
              <ProtectedRoute allowedRoles={['admin']}>
                <TestInsertForm />
              </ProtectedRoute>
            } />
            <Route path="/public-vetrine" element={<PublicVetrine />} />
            <Route path="/dev-debug" element={<DevDebug />} />
            {/* 404 Fallback - MUST be last */}
            <Route path="*" element={<NotFound />} />
          </RouterRoutes>
        </AuthProvider>
      </ErrorBoundary>
    </HashRouter>
  );
};
export default Routes;
